import { ImportButton } from "@/components/ImportButton";

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { AlertTriangle, Download, FileSpreadsheet, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { Upload } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

export default function CashFlow() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [year, setYear] = useState(2026);
  const [cfg, setCfg] = useState(null);

  const { data } = useQuery({
    queryKey: ["cashflow", year],
    queryFn: async () => {
      const d = (await api.get(`/finance/cashflow?year=${year}`)).data;
      setCfg({ year, ...d.config });
      return d;
    },
  });

  const saveCfg = useMutation({
    mutationFn: async () => (await api.put("/finance/cash-config", cfg)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cashflow"] }); toast.success("Οι ρυθμίσεις ταμείου αποθηκεύτηκαν"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const rows = data?.rows || [];
  const chart = rows.map((r) => ({ name: r.label.slice(0, 3), Εισπράξεις: r.total_receipts, Πληρωμές: r.total_payments, Υπόλοιπο: r.closing }));
  const num = "mt-1 w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm text-right tabular outline-none focus:border-[#45423C]";
  const lab = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold";
  const VAT_LABELS = { room: "Room", fnb_food: "F&B Food", fnb_drinks: "F&B Drinks", extra: "Extra", fee: "Fee", product: "Product", other: "Other" };

  return (
    <div className="space-y-6" data-testid="cashflow-page">
      <div className="flex justify-end"><ImportButton only={["obligations", "cheques"]} label="Ανέβασμα υποχρεώσεων / επιταγών" /></div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Ταμειακή Ροή</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Cash Flow 12 Μηνών</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Εισπράξεις κρατήσεων & τιμολογίων μείον προμηθευτές, μισθοδοσία, δόσεις και ΦΠΑ.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className={lab}>Έτος</label>
            <select data-testid="cf-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1 block rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
              {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button data-testid="cf-template" onClick={() => downloadXlsx("/finance/cashflow-template.xlsx", "TAF_Template_CashFlow.xlsx")}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#6E7A63] hover:border-[#6E7A63]">
            <Download className="h-4 w-4" /> Πρότυπο Excel
          </button>
          <button data-testid="cf-export" onClick={() => downloadXlsx(`/finance/cashflow.xlsx?year=${year}`, `TAF_CashFlow_${year}.xlsx`)}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
            <FileSpreadsheet className="h-4 w-4" /> Εξαγωγή
          </button>
        </div>
      </div>

      {data?.negative_months?.length > 0 && (
        <div className="rounded-2xl border border-[#991B1B]/30 bg-[#FEF2F2] p-5" data-testid="cf-negative-alert">
          <div className="flex items-center gap-2 text-[#991B1B]">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-semibold">Αρνητικό ταμείο σε {data.negative_months.length} μήνες: {data.negative_months.join(", ")}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Αρχικό Υπόλοιπο", data?.config?.opening_balance], ["Σύνολο Εισπράξεων", data?.totals?.receipts],
          ["Σύνολο Πληρωμών", data?.totals?.payments], ["Τελικό Υπόλοιπο", data?.closing_balance]].map(([l, v], i) => (
          <div key={l} className={`rounded-2xl p-5 ${i === 3 ? ((v ?? 0) < 0 ? "bg-[#991B1B] text-white" : "bg-[#45423C] text-white") : "bg-white border border-[#E3DED4]"}`} data-testid={`cf-kpi-${l}`}>
            <div className={`text-[10px] uppercase tracking-widest ${i === 3 ? "text-white/70" : "text-[#6F6A60]"}`}>{l}</div>
            <div className="font-serif text-2xl mt-2 tabular">{fmtEuro(v || 0)}</div>
          </div>
        ))}
      </div>

      {canEdit && cfg && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="cf-config">
          <h2 className="font-medium text-[#45423C] mb-4">Ρυθμίσεις ταμείου {year}</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className={lab}>Αρχικό υπόλοιπο (€)</label>
              <input data-testid="cf-opening" type="number" className={num} value={cfg.opening_balance}
                onChange={(e) => setCfg({ ...cfg, opening_balance: Number(e.target.value) })} />
            </div>
            <div>
              <label className={lab}>Προκαταβολή Flex (%)</label>
              <input data-testid="cf-flex" type="number" className={num} value={cfg.flex_deposit_pct}
                onChange={(e) => setCfg({ ...cfg, flex_deposit_pct: Number(e.target.value) })} />
            </div>
            <div>
              <label className={lab}>Πίστωση εξόδων (ημέρες)</label>
              <input data-testid="cf-exp-days" type="number" className={num} value={cfg.expense_credit_days}
                onChange={(e) => setCfg({ ...cfg, expense_credit_days: Number(e.target.value) })} />
            </div>
            <div>
              <label className={lab}>Πίστωση εσόδων (ημέρες)</label>
              <input data-testid="cf-inc-days" type="number" className={num} value={cfg.income_credit_days}
                onChange={(e) => setCfg({ ...cfg, income_credit_days: Number(e.target.value) })} />
            </div>
            <div className="flex items-end">
              <button data-testid="cf-save-config" onClick={() => saveCfg.mutate()} disabled={saveCfg.isPending}
                className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                <Save className="h-4 w-4" /> Αποθήκευση
              </button>
            </div>
          </div>
          <div className="mt-4 border-t border-[#E3DED4] pt-4" data-testid="cf-booking-vat-rates">
            <label className={lab}>Συντελεστές ΦΠΑ κρατήσεων ανά τύπο (%)</label>
            <div className="mt-2 flex flex-wrap gap-3">
              {Object.entries(cfg.booking_vat_rates || {}).map(([k, val]) => (
                <div key={k} className="w-28">
                  <div className="text-[11px] text-[#6F6A60] mb-1">{VAT_LABELS[k] || k}</div>
                  <input data-testid={`cf-vat-${k}`} type="number" step="0.5" className={num} value={val}
                    onChange={(e) => setCfg({ ...cfg, booking_vat_rates: { ...cfg.booking_vat_rates, [k]: Number(e.target.value) } })} />
                </div>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 mt-4 text-sm text-[#6F6A60]">
            <input data-testid="cf-include-vat" type="checkbox" checked={cfg.include_vat}
              onChange={(e) => setCfg({ ...cfg, include_vat: e.target.checked })} />
            Να συμπεριλαμβάνεται το μηνιαίο ΦΠΑ (πληρωμή τον επόμενο μήνα) — αν το ΦΠΑ είναι σε ρύθμιση δόσεων, ξεμαρκάρετέ το και καταχωρήστε τις δόσεις στις Υποχρεώσεις
          </label>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 md:p-6">
        <h2 className="font-medium text-[#45423C] mb-6">Εισπράξεις / Πληρωμές & Υπόλοιπο</h2>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chart} margin={{ left: -10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E3DED4" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#6F6A60" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#6F6A60" }} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E3DED4" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Εισπράξεις" fill="#6E7A63" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Πληρωμές" fill="#C86235" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="Υπόλοιπο" stroke="#B9AE9A" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4]"><h2 className="font-medium text-[#45423C]">Μηνιαία ταμειακή ροή</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs whitespace-nowrap">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-3 py-3">Μήνας</th>
                <th className="px-3 py-3 text-right">Αρχικό</th>
                <th className="px-3 py-3 text-right">Κρατήσεις</th>
                <th className="px-3 py-3 text-right">Τιμολόγια</th>
                <th className="px-3 py-3 text-right">Εισπράξεις</th>
                <th className="px-3 py-3 text-right">Προμηθευτές</th>
                <th className="px-3 py-3 text-right">Μισθοδοσία</th>
                <th className="px-3 py-3 text-right">Δόσεις</th>
                <th className="px-3 py-3 text-right">ΦΠΑ</th>
                <th className="px-3 py-3 text-right">Πληρωμές</th>
                <th className="px-3 py-3 text-right">Καθαρή Ροή</th>
                <th className="px-3 py-3 text-right">Τελικό</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.month} data-testid={`cf-row-${r.month}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-3 py-2 font-medium text-[#45423C]">{r.label}</td>
                  <td className="px-3 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(r.opening)}</td>
                  <td className="px-3 py-2 text-right tabular">{fmtEuro(r.receipts_bookings)}</td>
                  <td className="px-3 py-2 text-right tabular">{fmtEuro(r.receipts_invoices)}</td>
                  <td className="px-3 py-2 text-right tabular font-semibold text-[#6E7A63]">{fmtEuro(r.total_receipts)}</td>
                  <td className="px-3 py-2 text-right tabular">{fmtEuro(r.pay_suppliers)}</td>
                  <td className="px-3 py-2 text-right tabular">{fmtEuro(r.pay_payroll)}</td>
                  <td className="px-3 py-2 text-right tabular">{fmtEuro(r.pay_obligations)}</td>
                  <td className="px-3 py-2 text-right tabular">{fmtEuro(r.pay_vat)}</td>
                  <td className="px-3 py-2 text-right tabular font-semibold text-[#C86235]">{fmtEuro(r.total_payments)}</td>
                  <td className={`px-3 py-2 text-right tabular ${r.net < 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{fmtEuro(r.net)}</td>
                  <td className={`px-3 py-2 text-right tabular font-bold ${r.closing < 0 ? "text-[#991B1B]" : "text-[#45423C]"}`}>{fmtEuro(r.closing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[["Εισπρακτέα (aging)", data?.receivables_aging, "receivables"], ["Πληρωτέα (aging)", data?.payables_aging, "payables"]].map(([title, ag, key]) => (
          <div key={key} className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid={`aging-${key}`}>
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-baseline justify-between">
              <h2 className="font-medium text-[#45423C]">{title}</h2>
              <span className="text-sm tabular font-semibold">{fmtEuro(ag?.total)} <span className="text-xs text-[#6F6A60]">({ag?.count || 0})</span></span>
            </div>
            <div className="grid grid-cols-4 divide-x divide-[#E3DED4]">
              {["0-30", "31-60", "61-90", "90+"].map((b) => (
                <div key={b} className="p-4 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">{b} ημ.</div>
                  <div className="font-serif text-lg mt-1 tabular text-[#45423C]">{fmtEuro(ag?.[b])}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="cf-import-hint">
          <div>
            <h2 className="font-medium text-[#45423C]">Εισαγωγές από Excel</h2>
            <p className="text-sm text-[#6F6A60] mt-1">Κρατήσεις, υποχρεώσεις/δόσεις και επιταγές ανεβαίνουν σε ξεχωριστές καρτέλες στο Κέντρο Εισαγωγών.</p>
          </div>
          <Link to="/imports" data-testid="cf-goto-imports"
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A]">
            <Upload className="h-4 w-4" /> Εισαγωγές Excel
          </Link>
        </div>
      )}
    </div>
  );
}
